/*
  Madelynn Ave PDP effects. Vanilla JS, no dependencies, ~6 KB minified.
  Every effect is a custom element, so it initialises itself whenever its markup
  appears (first load, Shopify theme-editor block add/move, AJAX re-render) and
  cleans up when removed. Safe to load once in theme.liquid with `defer`.
*/
(() => {
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const rafThrottle = fn => { let q = false; return () => { if (q) return; q = true; requestAnimationFrame(() => { q = false; fn(); }); }; };

  /* Poster shield for lazily loaded videos. iOS Safari drops the native poster the moment
     play() is called and shows a blank frame until the first frame has decoded; with
     preload="none"/"metadata" that gap is a visible white flash the first time a video
     starts. A plain <img> copy of the poster sits on top of the video until the video has
     its first frame, then fades out and is removed (same technique as the customer-videos
     section). At that point the poster attribute is dropped too, so from then on the element
     shows its own first frame: iOS colour-manages the poster JPEG and the video differently,
     and swapping between them at play time reads as a tint change. Together with
     warmVideoNearViewport() the swap normally happens while the video is still off screen.
     Styles: .ma-poster-shield in ma-effects.css. */
  const posterShield = video => {
    const src = video.getAttribute('poster');
    if (!src || video._maShield) return;
    const img = document.createElement('img');
    img.className = 'ma-poster-shield'; img.src = src; img.alt = ''; img.setAttribute('aria-hidden', 'true'); img.decoding = 'async';
    video.after(img); video._maShield = img;
    const events = ['loadeddata', 'canplay', 'timeupdate'];
    const clear = () => {
      const hasFrame = video.readyState >= 2 || (!video.paused && video.currentTime > 0.05);
      if (!hasFrame) return;
      events.forEach(t => video.removeEventListener(t, clear));
      video.removeAttribute('poster');
      // Safari paints a paused frame only after a seek; a 1 ms nudge is enough.
      if (video.paused && video.currentTime === 0) { try { video.currentTime = 0.001; } catch (_) {} }
      img.classList.add('is-hidden');
      setTimeout(() => img.remove(), 500);
      video._maShield = null;
    };
    events.forEach(t => video.addEventListener(t, clear));
    if (video.readyState >= 2) clear();
  };

  /* Start buffering a lazy video once `target` is within about two screens of the viewport
     (one-shot), so its first frame is on screen before the video is reached. */
  const warmVideoNearViewport = (video, target = video) => {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      if (video.preload === 'auto') return;
      video.preload = 'auto';
      if (video.readyState === 0) video.load();
    }, { rootMargin: '200% 0px' });
    io.observe(target);
  };

  /* ---------------- Accordion ----------------
     <ma-accordion [single] [data-limit="4"]>
       <div data-acc-item [open]>
         <button data-acc-trigger>Title <span data-acc-sign></span></button>
         <div data-acc-panel><div><div>…content…</div></div></div>
       </div>
       <button data-acc-more data-label-more="All 11 questions" data-label-less="Show fewer"></button>
     </ma-accordion> */
  define('ma-accordion', class extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this._on = e => {
        const t = e.target.closest('[data-acc-trigger],[data-acc-more]');
        if (!t || t.closest('ma-accordion') !== this) return;
        if (t.hasAttribute('data-acc-more')) return this.toggleMore();
        this.set(t.closest('[data-acc-item]'));
      };
      this.addEventListener('click', this._on);
      this.items().forEach((it, i) => {
        const btn = it.querySelector('[data-acc-trigger]'), panel = it.querySelector('[data-acc-panel]');
        const id = panel.id || (panel.id = 'acc-' + Math.random().toString(36).slice(2, 8));
        btn.setAttribute('aria-controls', id);
        btn.setAttribute('aria-expanded', it.hasAttribute('open'));
      });
      this._expanded = false; this.applyLimit();
    }
    disconnectedCallback() { this.removeEventListener('click', this._on); this._on = null; }
    items() { return [...this.querySelectorAll('[data-acc-item]')].filter(n => n.closest('ma-accordion') === this); }
    set(item, force) {
      const open = force ?? !item.hasAttribute('open');
      if (open && this.hasAttribute('single')) this.items().forEach(o => o !== item && this.set(o, false));
      item.toggleAttribute('open', open);
      item.querySelector('[data-acc-trigger]').setAttribute('aria-expanded', open);
    }
    applyLimit() {
      const lim = parseInt(this.dataset.limit, 10), more = this.querySelector('[data-acc-more]');
      if (!lim) return;
      this.items().forEach((it, i) => { it.toggleAttribute('data-acc-extra', i >= lim); it.hidden = i >= lim && !this._expanded; });
      if (more) more.textContent = this._expanded ? more.dataset.labelLess : more.dataset.labelMore;
    }
    toggleMore() { this._expanded = !this._expanded; this.applyLimit(); }
  });

  /* ---------------- Tabs with crossfade ----------------
     <ma-tabs>
       <div data-tab-stage><img data-tab-image data-active …><img data-tab-image …><img data-tab-image …></div>
       <div data-tab-text><p data-tab-head></p><p data-tab-body></p></div>
       <div role="tablist"><button role="tab" data-head="…" data-body="…">Painted</button>…</div>
     </ma-tabs>
     Any [data-tab-image] may be a <video muted playsinline loop preload="metadata" poster="…">.
     The active tab's video plays while the stage is at least 50% on screen; inactive or
     off-screen videos pause. Reduced motion: videos stay on their poster. */
  define('ma-tabs', class extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this.tabs = [...this.querySelectorAll('[role=tab]')];
      this.imgs = [...this.querySelectorAll('[data-tab-image]')];
      this.text = this.querySelector('[data-tab-text]');
      this._on = e => { const t = e.target.closest('[role=tab]'); if (t && this.tabs.includes(t)) this.select(this.tabs.indexOf(t), true); };
      this._key = e => {
        const i = this.tabs.indexOf(document.activeElement); if (i < 0) return;
        const n = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : null;
        if (n === null) return; e.preventDefault(); this.select((n + this.tabs.length) % this.tabs.length, true); this.tabs[this.index].focus();
      };
      this.addEventListener('click', this._on); this.addEventListener('keydown', this._key);
      this.imgs.forEach(m => { if (m.tagName === 'VIDEO') { m.muted = true; m.playsInline = true; m.setAttribute('playsinline', ''); m.loop = true; posterShield(m); } });
      const stage = this.querySelector('[data-tab-stage]');
      if (stage && this.imgs.some(m => m.tagName === 'VIDEO')) {
        this._io = new IntersectionObserver(([e]) => { this._inView = e.intersectionRatio >= .5; this.media(); }, { threshold: [0, .5] });
        this._io.observe(stage);
        this._hid = () => this.media(); document.addEventListener('visibilitychange', this._hid);
      }
      // Warm the other images after first paint so the first tap never waits on the network.
      // Images only: a tab video keeps the preload="metadata" set in Liquid and loads when it plays.
      const warm = () => this.imgs.forEach(img => { if (img.tagName === 'VIDEO') return; img.loading = 'eager'; img.decode?.().catch(() => {}); });
      'requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 2000 }) : setTimeout(warm, 1200);
      this.select(Math.max(0, this.tabs.findIndex(t => t.getAttribute('aria-selected') === 'true')), false);
      // Every tab video, not only the active one: a tab chosen later then already shows its
      // own first frame instead of a poster that has to be swapped out when it starts.
      this.imgs.forEach(m => { if (m.tagName === 'VIDEO') warmVideoNearViewport(m, stage || this); });
    }
    disconnectedCallback() {
      this.removeEventListener('click', this._on); this.removeEventListener('keydown', this._key); this._on = null; clearTimeout(this._t);
      this._io?.disconnect(); document.removeEventListener('visibilitychange', this._hid); this.imgs?.forEach(m => m.tagName === 'VIDEO' && m.pause());
    }
    media() {
      this.imgs.forEach((m, k) => {
        if (m.tagName !== 'VIDEO') return;
        const on = k === this.index && this._inView && !document.hidden && !reduced();
        if (on) { const p = m.play(); p && p.catch(() => {}); } else m.pause();
      });
    }
    select(i, animate) {
      if (i === this.index) return; this.index = i;
      this.tabs.forEach((t, k) => { t.setAttribute('aria-selected', k === i); t.tabIndex = k === i ? 0 : -1; });
      this.imgs.forEach((img, k) => img.toggleAttribute('data-active', k === i));
      const active = this.imgs[i];
      if (active && active.tagName === 'VIDEO' && active.preload !== 'auto') active.preload = 'auto'; // it plays next
      this.media?.();
      const tab = this.tabs[i], apply = () => {
        const h = this.querySelector('[data-tab-head]'), b = this.querySelector('[data-tab-body]');
        if (h && tab.dataset.head) h.textContent = tab.dataset.head;
        if (b && tab.dataset.body) b.textContent = tab.dataset.body;
        this.text?.removeAttribute('data-fading');
      };
      if (!animate || !this.text || reduced()) return apply();
      clearTimeout(this._t); this.text.setAttribute('data-fading', ''); this._t = setTimeout(apply, 160);
    }
  });

  /* ---------------- Gallery carousel ----------------
     <ma-gallery>
       <div data-gallery-track><figure>…</figure>×N</div>
       <div class="bar"><div data-gallery-fill></div></div><span data-gallery-count></span>
       <button data-gallery-prev></button><button data-gallery-next></button>  (optional)
     </ma-gallery>
     Slides may contain <video muted playsinline loop preload="metadata" poster="…">. A video plays
     automatically while any part of its slide is visible (in the viewport and in the track) and
     pauses once it has left. Reduced motion: videos stay paused on their poster. */
  define('ma-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      this.track = this.querySelector('[data-gallery-track]');
      this.fill = this.querySelector('[data-gallery-fill]');
      this.count = this.querySelector('[data-gallery-count]');
      this._on = rafThrottle(() => this.sync());
      this._click = e => {
        if (e.target.closest('[data-gallery-next]')) this.go(this.index + 1);
        else if (e.target.closest('[data-gallery-prev]')) this.go(this.index - 1);
      };
      this.track.addEventListener('scroll', this._on, { passive: true });
      this.addEventListener('click', this._click);
      this._ro = new ResizeObserver(this._on); this._ro.observe(this.track);
      this.videos = [...this.track.querySelectorAll('video')];
      if (this.videos.length) {
        this.videos.forEach(v => {
          v.muted = true; v.playsInline = true; v.setAttribute('playsinline', ''); v.loop = true;
          // Same first-frame treatment as the other video sections (8 Oct 2026): the poster copy
          // stays on top until the first frame has decoded, so play() never shows a blank frame
          // or a poster-to-video tint change, and buffering starts while the gallery is still
          // two screens away.
          posterShield(v); warmVideoNearViewport(v, this);
        });
        const opts = { threshold: [0] };
        const play = v => { if (reduced() || !v.paused) return; const p = v.play(); p && p.catch(() => {}); };
        // Two observers: one against the viewport, one against the track, so a video plays while
        // any part of its slide is on screen and inside the carousel (8 Oct 2026; was 60% of it),
        // and pauses once it has left entirely.
        this._vis = new Map();
        const judge = v => { const s = this._vis.get(v) || {}; if (s.page && s.track) play(v); else if (!v.paused) v.pause(); };
        const mk = root => new IntersectionObserver(es => es.forEach(e => {
          const s = this._vis.get(e.target) || {}; s[root ? 'track' : 'page'] = e.isIntersecting && e.intersectionRatio > 0; this._vis.set(e.target, s); judge(e.target);
        }), { ...opts, root });
        this._vioPage = mk(null); this._vioTrack = mk(this.track);
        this.videos.forEach(v => { this._vioPage.observe(v); this._vioTrack.observe(v); });
        this._hid = () => document.hidden ? this.videos.forEach(v => v.pause()) : this.videos.forEach(judge);
        document.addEventListener('visibilitychange', this._hid);
      }
      this.index = -1; this.sync();
    }
    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this._on); this.removeEventListener('click', this._click); this._ro?.disconnect(); this._on = null;
      this._vioPage?.disconnect(); this._vioTrack?.disconnect(); document.removeEventListener('visibilitychange', this._hid);
      this.videos?.forEach(v => v.pause());
    }
    slides() { return [...this.track.children]; }
    sync() {
      const s = this.slides(); if (!s.length) return;
      const mid = this.track.scrollLeft + this.track.clientWidth / 2;
      let best = 0, d = Infinity;
      s.forEach((el, i) => { const c = el.offsetLeft + el.offsetWidth / 2, dd = Math.abs(c - mid); if (dd < d) { d = dd; best = i; } });
      if (best === this.index) return; this.index = best;
      if (this.fill) this.fill.style.transform = 'scaleX(' + ((best + 1) / s.length) + ')';
      if (this.count) this.count.textContent = (best + 1) + ' / ' + s.length;
      s.forEach((el, i) => el.toggleAttribute('data-current', i === best));
      this.dispatchEvent(new CustomEvent('ma:gallery-change', { detail: { index: best }, bubbles: true }));
    }
    go(i) {
      const s = this.slides(), n = (i + s.length) % s.length, el = s[n];
      this.track.scrollTo({ left: el.offsetLeft - (this.track.clientWidth - el.offsetWidth) / 2, behavior: reduced() ? 'auto' : 'smooth' });
    }
  });

  /* ---------------- Pinned quote (Notes from the artist) ----------------
     The image layer stays centred in the viewport while the section scrolls past it
     (the section clips it), and the quote rides the centre of whatever part of the
     section is on screen. Runs only while the section is in view.
     <ma-pinned-quote style="height:620px">
       <div data-pq-layer><img …><div class="scrim"></div></div>
       <div data-pq-quote>…</div>
     </ma-pinned-quote> */
  define('ma-pinned-quote', class extends HTMLElement {
    connectedCallback() {
      if (this._io) return;
      // With CSS view timelines (ma-effects.css) the compositor drives the motion in step with
      // the scroll; the per-frame script below only runs where they are not supported.
      if (CSS.supports('animation-timeline: view()')) return;
      this.layer = this.querySelector('[data-pq-layer]'); this.quote = this.querySelector('[data-pq-quote]');
      this._loop = () => { this.frame(); if (this._live) this._raf = requestAnimationFrame(this._loop); };
      this._io = new IntersectionObserver(([e]) => {
        this._live = e.isIntersecting && !reduced();
        cancelAnimationFrame(this._raf);
        if (this._live) this._raf = requestAnimationFrame(this._loop); else this.frame();
      }, { rootMargin: '100px 0px' });
      this._io.observe(this);
    }
    disconnectedCallback() { this._io?.disconnect(); this._io = null; this._live = false; cancelAnimationFrame(this._raf); }
    frame() {
      const r = this.getBoundingClientRect(), vh = window.innerHeight, bh = this.offsetHeight;
      if (reduced()) { this.layer.style.transform = ''; this.quote.style.transform = 'translate3d(0,0,0) translateY(-50%)'; return; }
      const layerTop = (vh - bh) / 2 - r.top;
      const visTop = Math.max(0, -r.top), visBot = Math.min(bh, vh - r.top);
      const q = Math.max(0, Math.min(bh, (visTop + visBot) / 2)) - bh / 2;
      this.layer.style.transform = 'translate3d(0,' + layerTop.toFixed(1) + 'px,0)';
      this.quote.style.transform = 'translate3d(0,' + q.toFixed(1) + 'px,0) translateY(-50%)';
    }
  });

  /* ---------------- Autoplay video ----------------
     Plays a muted, looping, inline video while at least 50% of it is on screen; pauses otherwise
     and when the tab is hidden. Reduced motion: stays on the poster.
     <ma-autoplay><video muted playsinline loop preload="metadata" poster="…" src="…"></video></ma-autoplay> */
  define('ma-autoplay', class extends HTMLElement {
    connectedCallback() {
      if (this._io) return;
      this.v = this.querySelector('video'); if (!this.v) return;
      Object.assign(this.v, { muted: true, loop: true, playsInline: true }); this.v.setAttribute('playsinline', '');
      posterShield(this.v);
      warmVideoNearViewport(this.v, this);
      this._io = new IntersectionObserver(([e]) => { this._in = e.intersectionRatio >= .5; this.judge(); }, { threshold: [0, .5] });
      this._io.observe(this.v);
      this._hid = () => this.judge(); document.addEventListener('visibilitychange', this._hid);
    }
    disconnectedCallback() { this._io?.disconnect(); this._io = null; document.removeEventListener('visibilitychange', this._hid); this.v?.pause(); }
    judge() {
      const on = this._in && !document.hidden && !reduced();
      if (on) { const p = this.v.play(); p && p.then(() => this.setAttribute('data-playing', '')).catch(() => this.removeAttribute('data-playing')); }
      else { this.v.pause(); this.removeAttribute('data-playing'); }
    }
  });

  /* ---------------- Countdown ----------------
     <ma-countdown ends="2026-12-01T00:00:00-07:00" hide-when-done>
       <span data-cd="d"></span> <span data-cd="h"></span> <span data-cd="m"></span> <span data-cd="s"></span>
     </ma-countdown>
     Always pass a timezone offset in `ends` so every visitor sees the same end moment. */
  define('ma-countdown', class extends HTMLElement {
    connectedCallback() { this.tick(); }
    disconnectedCallback() { clearTimeout(this._t); }
    tick() {
      clearTimeout(this._t);
      const end = Date.parse(this.getAttribute('ends')); if (isNaN(end)) return;
      const ms = end - Date.now(), rem = Math.max(0, Math.floor(ms / 1000));
      const v = { d: Math.floor(rem / 86400), h: Math.floor(rem % 86400 / 3600), m: Math.floor(rem % 3600 / 60), s: rem % 60 };
      this.querySelectorAll('[data-cd]').forEach(el => { const s = String(v[el.dataset.cd]).padStart(2, '0'); if (el.textContent !== s) el.textContent = s; });
      if (rem <= 0) {
        if (this.hasAttribute('hide-when-done')) this.hidden = true;
        this.dispatchEvent(new CustomEvent('ma:countdown-end', { bubbles: true }));
        return;
      }
      this._t = setTimeout(() => this.tick(), (ms % 1000) + 20); // re-align to each second boundary
    }
  });

  /* ---------------- Selection state ----------------
     Wrap the customizer + CTA. Reads the theme's variant JSON, so it works with any
     variant picker that uses real radio/select inputs named by option.
     <ma-selection data-options="Frame,Size">
       <script type="application/json" data-variants>{{ product.variants | json }}</script>
       <div data-step="Frame"><span data-step-check></span>…radios name="Frame"…</div>
       <div data-step="Size"><span data-step-check></span>…radios name="Size"…</div>
       <span data-ma-piece></span>
       <span data-ma-from>From</span>
       <button data-ma-cta data-label-ready="Add to cart" data-label-wait="Please choose a frame & size." data-label-soldout="Sold out">…</button>
     </ma-selection>
     Emits `ma:selection` on document: { values, options, complete, variant, soldOut }. */
  define('ma-selection', class extends HTMLElement {
    connectedCallback() {
      if (this._on) return;
      try { this.variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]'); } catch { this.variants = []; }
      this.options = (this.dataset.options || '').split(',').map(s => s.trim()).filter(Boolean);
      this._on = () => this.update();
      this.addEventListener('change', this._on);
      this.update();
    }
    disconnectedCallback() { this.removeEventListener('change', this._on); this._on = null; }
    value(name) {
      const el = this.querySelector(`[name="${CSS.escape(name)}"]:checked, select[name="${CSS.escape(name)}"]`);
      return el && el.value ? el.value : null;
    }
    update() {
      const values = this.options.map(o => this.value(o));
      const complete = values.every(Boolean);
      this.options.forEach((o, i) => this.querySelector(`[data-step="${CSS.escape(o)}"]`)?.toggleAttribute('data-done', !!values[i]));
      const variant = complete ? this.variants.find(v => v.options.every((ov, i) => ov === values[i])) : null;
      const piece = this.querySelector('[data-ma-piece]'); if (piece) piece.textContent = values.map(v => v || '—').join(' · ');
      document.querySelectorAll('[data-ma-from]').forEach(el => el.hidden = values.some(Boolean));
      const cta = this.querySelector('[data-ma-cta]');
      const soldOut = complete && !!variant && variant.available === false;
      if (cta) {
        const ready = complete && !soldOut;
        cta.toggleAttribute('data-ready', ready); cta.disabled = !ready;
        const lbl = soldOut ? (cta.dataset.labelSoldout || 'Sold out') : ready ? cta.dataset.labelReady : cta.dataset.labelWait; if (lbl) cta.textContent = lbl;
      }
      const detail = { values, options: this.options, complete, variant, soldOut };
      window.__maSelection = detail;
      document.dispatchEvent(new CustomEvent('ma:selection', { detail }));
    }
  });

  /* ---------------- Sticky cart bar ----------------
     Appears once the main CTA has scrolled above the viewport bottom AND the gallery's
     centre has reached the middle of the screen. Hides again when scrolling back up.
     <ma-sticky-bar cta="[data-ma-cta]" gallery="ma-gallery" scroll-to="[data-step]" form="product-form-id" money-format="${{amount}}">
       <span>Woodland</span>
       <span data-state-show="incomplete">Please choose a frame &amp; size</span>
       <span data-state-show="complete"><span data-bar-spec></span> · <s data-state-show="sale" data-bar-compare></s> <span data-bar-price></span></span>
       <button data-bar-button data-label-add="Add to cart" data-label-choose="Choose {option}" data-label-soldout="Sold out"></button>
     </ma-sticky-bar> */
  define('ma-sticky-bar', class extends HTMLElement {
    connectedCallback() {
      if (this._on) return;

      this._on = rafThrottle(() => this.check());
      addEventListener('scroll', this._on, { passive: true });
      addEventListener('resize', this._on, { passive: true });

      this._syncEvent = () => this.queueSync();
      document.addEventListener('ma:selection', this._syncEvent);
      document.addEventListener('theme:variant:change', this._syncEvent);
      document.addEventListener('ma:pdp-price-updated', this._syncEvent);

      this._click = e => {
        if (e.target.closest('[data-bar-button]')) this.act();
      };
      this.addEventListener('click', this._click);

      this.setAttribute('data-state', 'incomplete');
      this.syncFromMain();
      this.check();
    }

    disconnectedCallback() {
      removeEventListener('scroll', this._on);
      removeEventListener('resize', this._on);
      document.removeEventListener('ma:selection', this._syncEvent);
      document.removeEventListener('theme:variant:change', this._syncEvent);
      document.removeEventListener('ma:pdp-price-updated', this._syncEvent);
      this.removeEventListener('click', this._click);
      this._ctaObserver?.disconnect();
      clearTimeout(this._syncTimer1);
      clearTimeout(this._syncTimer2);
      this._on = null;
      document.documentElement.classList.remove('ma-sticky-bar-visible');
      document.documentElement.style.removeProperty('--ma-sticky-bar-offset');
    }

    queueSync() {
      requestAnimationFrame(() => this.syncFromMain());
      clearTimeout(this._syncTimer1);
      clearTimeout(this._syncTimer2);
      this._syncTimer1 = setTimeout(() => this.syncFromMain(), 70);
      this._syncTimer2 = setTimeout(() => this.syncFromMain(), 220);
    }

    mainCta() {
      // Reuse the button we already observe; it only changes when the section re-renders.
      if (this._observedCta && this._observedCta.isConnected) return this._observedCta;
      const selector = this.getAttribute('cta') || '[data-ma-cta]';
      let buttons = [];
      try { buttons = [...document.querySelectorAll(selector)]; } catch (_) {}
      if (!buttons.length) buttons = [...document.querySelectorAll('[data-add-to-cart][data-ma-guided-atc="true"]')];
      return buttons.find(btn => btn.getClientRects().length && getComputedStyle(btn).display !== 'none') || buttons[0] || null;
    }

    customizer() {
      const scope = document.querySelector('[data-ma-handoff-pdp="true"]') || document;
      const all = [...scope.querySelectorAll('[data-ma-pdp-mobile]')];
      return all.find(el => el.getClientRects().length && getComputedStyle(el).display !== 'none') || all[0] || null;
    }

    selectedValue(kind) {
      const customizer = this.customizer();
      if (!customizer) return '';
      const selected = customizer.querySelector(`[data-ma-option="${kind}"].is-selected, [data-ma-option="${kind}"][aria-pressed="true"]`);
      return selected ? (selected.dataset.value || selected.textContent || '').trim() : '';
    }

    currentVariant() {
      const cta = this.mainCta();
      const form = cta?.closest('form[data-product-form]') || document.querySelector('[data-ma-handoff-pdp="true"] form[data-product-form]');
      const id = form?.querySelector('input[name="id"]')?.value;
      if (!id || !Array.isArray(window.productVariantsData)) return null;
      return window.productVariantsData.find(v => String(v.id) === String(id)) || null;
    }

    buttonText(cta) {
      if (!cta) return '';
      const node = cta.querySelector('[data-add-to-cart-text]');
      return ((node && node.textContent) || cta.getAttribute('aria-label') || cta.textContent || '').replace(/\s+/g, ' ').trim();
    }

    readState() {
      const cta = this.mainCta();
      const frame = this.selectedValue('frame');
      const size = this.selectedValue('size');
      const complete = !!(frame && size);
      const variant = this.currentVariant();
      const text = this.buttonText(cta);
      const soldOut = complete && (!!cta?.disabled) && /sold\s*out|unavailable/i.test(text);
      return { values: [frame || null, size || null], options: ['Frame', 'Size'], complete, variant, soldOut, cta, text };
    }

    observeCta(cta) {
      if (!cta || this._observedCta === cta) return;
      this._ctaObserver?.disconnect();
      this._observedCta = cta;
      this._ctaObserver = new MutationObserver(() => this.queueSync());
      this._ctaObserver.observe(cta, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-label', 'class'] });
    }

    check() {
      const cta = this.mainCta();
      if (!cta) return;
      this.observeCta(cta);

      const mobile = window.matchMedia('(max-width: 768px)').matches;
      const show = mobile && cta.getBoundingClientRect().bottom <= 0;
      const currentlyVisible = this.hasAttribute('data-visible');
      // check() runs on every scroll frame; only a hidden<->visible transition needs DOM work.
      // Selection and price changes reach the bar through queueSync() while it is visible.
      if (show === currentlyVisible) return;

      if (show) {
        this.toggleAttribute('data-visible', true);
        this.removeAttribute('inert');
        this.syncFromMain();
        const height = Math.ceil(this.getBoundingClientRect().height || 0);
        document.documentElement.style.setProperty('--ma-sticky-bar-offset', `${height}px`);
        document.documentElement.classList.add('ma-sticky-bar-visible');
      } else {
        document.documentElement.classList.remove('ma-sticky-bar-visible');
        document.documentElement.style.removeProperty('--ma-sticky-bar-offset');
        this.removeAttribute('data-visible');
        this.setAttribute('inert', '');
      }
    }

    money(cents) {
      const f = this.getAttribute('money-format') || '${{amount}}';
      const amt = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: cents % 100 ? 2 : 0, maximumFractionDigits: 2 });
      return f.replace(/\{\{\s*amount\w*\s*\}\}/, amt);
    }

    livePricing() {
      const scope = document.querySelector('[data-ma-handoff-pdp="true"]') || document;
      const row = scope.querySelector('#pdp-price-row');
      if (!row) return null;
      const finalEl = row.querySelector('.pdp-final-price') || row.querySelector('[data-product-price]') || row.querySelector('#pdp-price-display');
      const compareEl = row.querySelector('.pdp-original-price') || row.querySelector('#pdp-compare-price') || row.querySelector('[data-compare-price]');
      const clean = el => (el?.textContent || '').replace(/\s+/g, ' ').trim();
      const number = text => {
        const n = parseFloat(String(text || '').replace(/[^0-9.,-]/g, '').replace(/,/g, ''));
        return Number.isFinite(n) ? n : NaN;
      };
      const priceText = clean(finalEl);
      const compareText = clean(compareEl);
      const priceNumber = number(priceText);
      const compareNumber = number(compareText);
      const sale = !!(priceText && compareText && Number.isFinite(priceNumber) && Number.isFinite(compareNumber) && compareNumber > priceNumber);
      return { priceText, compareText, sale };
    }

    fallbackPrompt(state) {
      const cta = state.cta;
      const hasFrame = !!state.values[0];
      const hasSize = !!state.values[1];
      if (!hasFrame && !hasSize) return cta?.dataset.maPromptInitial || 'Please choose a frame & Size.';
      if (hasFrame && !hasSize) return cta?.dataset.maPromptFrame || 'Please choose a size.';
      if (!hasFrame && hasSize) return cta?.dataset.maPromptSize || 'Please choose a frame.';
      if (state.soldOut) return cta?.dataset.maSoldOutLabel || 'Sold out';
      return cta?.dataset.maReadyLabel || 'ADD TO CART';
    }

    syncFromMain() {
      const state = this.readState();
      this._last = state;
      this.observeCta(state.cta);
      this.setAttribute('data-state', state.complete ? 'complete' : 'incomplete');

      const variant = state.variant;
      const livePricing = this.livePricing();
      const variantSale = !!(variant && variant.compare_at_price && variant.compare_at_price > variant.price);
      const sale = livePricing ? livePricing.sale : variantSale;
      this.toggleAttribute('data-sale', sale);
      this.toggleAttribute('data-soldout', !!state.soldOut);

      const q = s => this.querySelector(s);
      const chosen = state.values.filter(Boolean);
      const anySelection = chosen.length > 0;
      const specWrap = q('[data-bar-spec-wrap]');
      const spec = q('[data-bar-spec]');
      const from = q('[data-bar-from]');
      const price = q('[data-bar-price]');

      if (spec) spec.textContent = chosen.join(' · ');
      if (specWrap) specWrap.hidden = !anySelection;
      if (from) from.hidden = anySelection;
      // One line: "frame · size · price". The struck-through original is not shown here (it does not
      // fit beside the choices on a phone); data-sale colours the price red instead.
      if (price) {
        if (livePricing && livePricing.priceText) price.textContent = livePricing.priceText;
        else if (variant) price.textContent = this.money(variant.price);
      }
      const btn = q('[data-bar-button]');
      if (!btn) return;

      // Sticky button has its own concise labels; the main PDP CTA is unchanged.
      const hasFrame = !!state.values[0];
      const hasSize = !!state.values[1];
      const ready = !!(hasFrame && hasSize && state.cta && !state.cta.disabled && !state.soldOut);

      // Labels come from the section settings (data-label-* on the button).
      const chooseLabel = option => (btn.dataset.labelChoose || 'Choose {option}').replace('{option}', option);
      let label;
      if (state.soldOut) label = btn.dataset.labelSoldout || 'Sold out';
      else if (!hasFrame) label = chooseLabel('frame');
      else if (!hasSize) label = chooseLabel('size');
      else label = btn.dataset.labelAdd || 'Add to cart';
      if (btn.textContent !== label) btn.textContent = label;

      btn.disabled = !ready;
      btn.setAttribute('aria-disabled', btn.disabled ? 'true' : 'false');
      btn.toggleAttribute('data-ready', ready);
    }

    act() {
      const state = this.readState();
      this._last = state;

      if (state.complete && state.cta && !state.cta.disabled && !state.soldOut) {
        // Trigger the exact working PDP button. It already owns the selected
        // variant id, cart request, redirect/cart-drawer logic and validation.
        state.cta.click();
        return;
      }

      const missing = !state.values[0] ? 'Frame' : !state.values[1] ? 'Size' : '';
      const customizer = this.customizer();
      const target = missing
        ? customizer?.querySelector(`[data-step="${missing}"]`)
        : customizer?.querySelector('[data-step]');
      if (!target) return;
      const y = target.getBoundingClientRect().top + scrollY - 24;
      scrollTo({ top: y, behavior: reduced() ? 'auto' : 'smooth' });
    }
  });


})();
